import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import adminService from '../../services/adminService';
import type { IntegrationHealth, IntegrationHealthStatus } from '../../services/adminService';

const AUTO_REFRESH_MS = 60_000;

const STATUS_META: Record<IntegrationHealthStatus, { label: string; token: string }> = {
  healthy: { label: 'Healthy', token: 'completed' },
  unhealthy: { label: 'Unhealthy', token: 'attention' },
  unconfigured: { label: 'Not configured', token: 'enrolled' },
};

// Failures surface first, chronic not-configured entries sink to the bottom
const SEVERITY_ORDER: Record<IntegrationHealthStatus, number> = {
  unhealthy: 0,
  healthy: 1,
  unconfigured: 2,
};

// Where to go when an integration is failing. Only unambiguous consoles are
// listed; extend per key as needed.
const DASHBOARD_LINKS: Record<string, string> = {
  clerk: 'https://dashboard.clerk.com',
  postgresql: 'https://portal.azure.com',
  'functions-cpe-requirements': 'https://portal.azure.com',
  'functions-scheduled': 'https://portal.azure.com',
};

function StatusCell({ status }: { status: IntegrationHealthStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.unconfigured;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          flexShrink: 0,
          // Hollow dot for not-configured: present, but not a live signal
          ...(status === 'unconfigured'
            ? { border: '1.5px solid', borderColor: `status.${meta.token}.dot` }
            : { bgcolor: `status.${meta.token}.dot` }),
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 600, color: `status.${meta.token}.fg` }}>
        {meta.label}
      </Typography>
    </Box>
  );
}

function formatCheckedAt(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  return `${minutes}m ago`;
}

function formatFailingNames(names: string[]): string {
  const list = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(names);
  return `${list} ${names.length === 1 ? 'is' : 'are'} failing`;
}

export default function IntegrationsHealth() {
  const [integrations, setIntegrations] = useState<IntegrationHealth[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Re-render every 15s so the "checked Xs ago" label stays current between refreshes
  const [, setNowTick] = useState(0);
  const inFlightRef = useRef(false);

  const fetchHealth = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setRefreshing(true);
    try {
      const data = await adminService.getIntegrationsHealth();
      setIntegrations(data);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to check integrations health');
    } finally {
      inFlightRef.current = false;
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    // Skip auto-refresh while the tab is hidden; catch up as soon as it's visible again
    const refreshTimer = setInterval(() => {
      if (!document.hidden) fetchHealth();
    }, AUTO_REFRESH_MS);
    const tickTimer = setInterval(() => setNowTick((t) => t + 1), 15_000);
    const onVisibilityChange = () => {
      if (!document.hidden) fetchHealth();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(refreshTimer);
      clearInterval(tickTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchHealth]);

  const sorted = integrations
    ? [...integrations].sort(
        (a, b) => SEVERITY_ORDER[a.status] - SEVERITY_ORDER[b.status] || a.name.localeCompare(b.name),
      )
    : null;
  const failing = sorted?.filter((i) => i.status === 'unhealthy') ?? [];
  const healthyCount = sorted?.filter((i) => i.status === 'healthy').length ?? 0;
  const unconfiguredCount = sorted?.filter((i) => i.status === 'unconfigured').length ?? 0;
  const total = sorted?.length ?? 0;
  const checkedAt = sorted?.[0]?.checkedAt ?? null;

  return (
    <Box>
      {/* Summary + refresh. Loud when something is down, quiet otherwise. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.75, flexWrap: 'wrap' }}>
        <Box aria-live="polite" sx={{ flex: 1, minWidth: 240 }}>
          {failing.length > 0 ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: 'status.attention.bg',
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'status.attention.dot', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'status.attention.fg' }}>
                {formatFailingNames(failing.map((i) => i.name))}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, py: 0.75 }}>
              {sorted && (
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'status.completed.dot', flexShrink: 0 }} />
              )}
              <Typography variant="body2" color="text.secondary">
                {sorted
                  ? unconfiguredCount > 0
                    ? `${healthyCount} of ${total} integrations healthy · ${unconfiguredCount} not configured`
                    : `All ${total} integrations healthy`
                  : 'Checking integration status…'}
              </Typography>
            </Box>
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
          onClick={fetchHealth}
          disabled={refreshing}
        >
          {refreshing ? 'Checking…' : 'Refresh'}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {checkedAt && `checked ${formatCheckedAt(checkedAt)} · `}
        auto-refreshes every 60 seconds
        {error && sorted && ' · showing last successful check'}
      </Typography>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Empty */}
      {sorted && sorted.length === 0 && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Typography>No integrations are registered for health checks.</Typography>
        </Paper>
      )}

      {/* The ledger */}
      {(!sorted || sorted.length > 0) && !(!sorted && error) && (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 150 }}>Status</TableCell>
                <TableCell>Integration</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  Latency
                </TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!sorted
                ? Array.from({ length: 6 }, (_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton width={90} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={140} />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton width={56} sx={{ ml: 'auto' }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="60%" />
                      </TableCell>
                    </TableRow>
                  ))
                : sorted.map((integration) => (
                    <TableRow key={integration.key} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell>
                        <StatusCell status={integration.status} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={integration.description} placement="top-start" enterDelay={400}>
                          <Typography variant="body2" sx={{ fontWeight: 600, display: 'inline' }}>
                            {integration.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                        {integration.latencyMs != null ? `${integration.latencyMs.toLocaleString()} ms` : '—'}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: integration.status === 'unhealthy' ? 'status.attention.fg' : 'text.secondary',
                          wordBreak: 'break-word',
                        }}
                      >
                        {integration.message}
                        {integration.status === 'unhealthy' && DASHBOARD_LINKS[integration.key] && (
                          <>
                            {integration.message && ' · '}
                            <Link
                              href={DASHBOARD_LINKS[integration.key]}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                            >
                              Open dashboard
                            </Link>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
